/**
 * Archived projects (services/ProjectArchiveService): the Projects list's "Archived (n)" view, the "⋯" menu with
 * Archive… on a project row or card, and the banner an archived project's Home shows.
 *
 * - ArchivedProjectsList: the person's archived projects (theirs and the ones shared with them) in the list's own
 *   columns: name, description, Archived, the date, the owner, made with; each can be opened, and (its owner) be
 *   unarchived or deleted; in select mode several at once (Unarchive n, Delete n). Deleting goes through the page's own
 *   delete confirmation (`onDelete(ids)`).
 * - ProjectArchiveMenu: "⋯" › Archive… (`onArchive`).
 * - ArchivedBanner: "Archived" on the project's Home, with Unarchive for its owner.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Dropdown } from 'antd';
import { Archive, DotsThree } from '@phosphor-icons/react';
import './ArchivedProjects.css';

const ownerOf = (project) => (project.isOwner ? 'Me' : (project.sharedBy || project.creatorName || '—'));

export function ArchivedProjectsList({
  projects, formatDate, selectionMode = false, selectedIds, onSelectToggle, onSelectAll, onOpen, onUnarchive, onDelete, busy = false,
}) {
  if (projects.length === 0) {
    return (
      <div className="projects-empty-favorites" data-testid="archived-empty">
        <p>No archived projects. Archive one from its ⋯ menu to hide it without deleting it.</p>
      </div>
    );
  }
  const allSelected = projects.every((project) => selectedIds.has(project._id));
  return (
    <div className="projects-list projects-archived-list" data-testid="archived-projects">
      <div className={`list-header ${selectionMode ? '' : 'no-select-column'}`}>
        {selectionMode && (
          <div className="list-column select-column">
            <input aria-label="Select all archived projects" checked={allSelected} onChange={onSelectAll} type="checkbox" />
          </div>
        )}
        <div className="list-column name-column">NAME</div>
        <div className="list-column description-column">DESCRIPTION</div>
        <div className="list-column tech-column">STATUS</div>
        <div className="list-column date-column">ARCHIVED</div>
        <div className="list-column owner-column">OWNER</div>
        <div className="list-column env-column">MADE WITH</div>
        <div className="list-column actions-column"></div>
      </div>
      {projects.map((project) => (
        <div
          className={`list-row project-row ${selectionMode ? '' : 'no-select-column'}`}
          data-testid="archived-project"
          key={project._id}
          onClick={() => onOpen(project)}
        >
          {selectionMode && (
            <div className="list-column select-column">
              <input
                aria-label={`Select ${project.name}`}
                checked={selectedIds.has(project._id)}
                onChange={() => onSelectToggle(project._id)}
                onClick={(event) => event.stopPropagation()}
                type="checkbox"
              />
            </div>
          )}
          <div className="list-column name-column">
            <div className="project-icon-placeholder">{project.name?.substring(0, 2).toUpperCase() || 'PR'}</div>
            <div className="project-list-info">
              <span className="project-list-name">{project.name || 'Untitled Project'}</span>
            </div>
          </div>
          <div className="list-column description-column" title={project.description || ''}>
            <span className="project-list-description">{project.description || '—'}</span>
          </div>
          <div className="list-column tech-column"><span className="sbx-pill project-archived-pill">Archived</span></div>
          <div className="list-column date-column">{formatDate(project.archivedAt)}</div>
          <div className="list-column owner-column">{ownerOf(project)}</div>
          <div className="list-column env-column">{project.createdWith || '—'}</div>
          <div className="list-column actions-column" onClick={(event) => event.stopPropagation()}>
            {project.isOwner ? (
              <>
                <button className="bulk-clear-btn archived-row-btn" disabled={busy} onClick={() => onUnarchive([project._id])} type="button">Unarchive</button>
                <button className="bulk-delete-btn archived-row-btn" disabled={busy} onClick={() => onDelete([project._id])} type="button">Delete…</button>
              </>
            ) : (
              <span className="archived-row-note" title="Only its owner can unarchive or delete it">Archived by its owner</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

ArchivedProjectsList.propTypes = {
  busy: PropTypes.bool,
  formatDate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onSelectToggle: PropTypes.func.isRequired,
  onUnarchive: PropTypes.func.isRequired,
  projects: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  selectionMode: PropTypes.bool,
};

/** "⋯" on a project row or card: Archive… (its owner's). */
export function ProjectArchiveMenu({ name, onArchive }) {
  return (
    <Dropdown
      menu={{ items: [{ key: 'archive', label: 'Archive…', icon: <Archive aria-hidden size={14} /> }], onClick: ({ domEvent }) => { domEvent?.stopPropagation(); onArchive(); } }}
      placement="bottomRight"
      trigger={['click']}
    >
      <button
        aria-label={`More actions for ${name || 'this project'}`}
        className="list-favorite-button project-more-button"
        data-testid="project-more-actions"
        onClick={(event) => event.stopPropagation()}
        title="More actions"
        type="button"
      >
        <DotsThree aria-hidden size={16} weight="bold" />
      </button>
    </Dropdown>
  );
}

ProjectArchiveMenu.propTypes = {
  name: PropTypes.string,
  onArchive: PropTypes.func.isRequired,
};

/** On an archived project's Home: what it means, and Unarchive for its owner. */
export function ArchivedBanner({ canUnarchive, onUnarchive, busy = false, archivedAt = null }) {
  const when = archivedAt && !Number.isNaN(Date.parse(archivedAt))
    ? ` on ${new Date(archivedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : '';
  return (
    <div className="project-archived-banner" data-testid="archived-banner" role="status">
      <Archive aria-hidden size={18} />
      <span>
        <strong>Archived</strong>{when}. It is hidden from Projects, Home, workspaces, search and AI tools; its services stay stopped.
        {canUnarchive ? '' : ' Only its owner can unarchive it.'}
      </span>
      {canUnarchive && (
        <button className="ph-btn" disabled={busy} onClick={onUnarchive} type="button">{busy ? 'Unarchiving…' : 'Unarchive'}</button>
      )}
    </div>
  );
}

ArchivedBanner.propTypes = {
  archivedAt: PropTypes.string,
  busy: PropTypes.bool,
  canUnarchive: PropTypes.bool.isRequired,
  onUnarchive: PropTypes.func.isRequired,
};
